import { useEffect, useState } from 'react';
import { DEFAULT_GAME_ID } from '../../../shared/domain.ts';
import type { GameId } from '../../../shared/domain.ts';
import { fetchGames } from '../../lib/api.ts';
import type { CatalogueEntry } from '../../lib/api.ts';

export interface GamePickerProps {
  readonly value: GameId;
  readonly onChange: (game: GameId) => void;
  readonly disabled?: boolean;
}

type CatalogueState =
  | { status: 'loading' }
  | { status: 'ready'; entries: CatalogueEntry[] }
  | { status: 'error' };

/**
 * Chooses which published game to queue for or host.
 *
 * This is the join between the two halves of the product. The authoring engine
 * could store, validate and publish a spec long before anything could start a
 * match under it — the lobby sent a hard-coded `'connect4'` — so a subscriber
 * could build a game that literally nobody, including them, was able to play.
 *
 * It renders nothing at all while there is only one game to choose from: a
 * select with a single option is a decision the player does not have.
 */
export function GamePicker({ value, onChange, disabled }: GamePickerProps) {
  const [state, setState] = useState<CatalogueState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetchGames(controller.signal)
      .then((entries) => setState({ status: 'ready', entries }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('game catalogue failed', error);
        setState({ status: 'error' });
      });
    return () => controller.abort();
  }, []);

  // A failed catalogue is not a blocked lobby: the reference game is compiled
  // in and always playable, so the player keeps a working "Find a match".
  if (state.status !== 'ready' || state.entries.length <= 1) return null;

  return (
    <div className="gamePicker">
      <label className="gamePicker__label" htmlFor="game-picker">
        Game
      </label>
      <select
        id="game-picker"
        className="gamePicker__select"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {state.entries.map((entry) => (
          <option key={entry.gameId} value={entry.gameId}>
            {entry.title}
            {entry.gameId === DEFAULT_GAME_ID ? '' : ' (community)'}
          </option>
        ))}
      </select>
    </div>
  );
}
